import clsx from 'clsx';
import { Breadcrumbs } from 'components/bredcrambs';
import { Layout } from 'components/layout';
import dynamic from 'next/dynamic';
import { LoadMoreButton } from 'components/load-more-button';
import { CustomMetadata } from 'components/metadata';
import { SeeMore } from 'components/see-more';
import { CardsSkeleton } from 'components/slider/cards/medium';
import { StreamerCard } from 'components/streamer-card';
import { Title } from 'components/title';
import { allMetadata } from 'constants/metadata';
import { Routes } from 'constants/routes';
import { streamerPage } from 'constants/routes-breadcrumbs';
import {
  IFilter,
  IRegionItem,
  IStreamer,
  IStreamerCollection,
  IStreamerReview,
} from 'constants/types';
import { useStreamersPagination } from 'hooks/useStreamersPagination';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listStreamerLanguages } from 'lib/api/streamers';
import { Search } from '../home/search';
import styles from './styles.module.css';

const StreamersDescriptionSection = dynamic(
  () => import('./description').then((mod) => mod.StreamersDescription),
  { loading: () => null, ssr: false }
);
const FAQSection = dynamic(
  () => import('components/casino-faq').then((mod) => mod.FAQComponent),
  { loading: () => null, ssr: false }
);
const StreamersFiltersBar = dynamic(
  () => import('./filters-bar').then((mod) => mod.StreamersFiltersBar),
  { loading: () => <div className={styles.filterPlaceholder} />, ssr: false }
);

const LatestReviewsSection = dynamic(
  () =>
    import('../home/latest-casino-reviews').then(
      (mod) => mod.LatestReviewsLine
    ),
  { loading: () => null, ssr: false }
);

const StreamerCollectionsSection = dynamic(
  () => import('./collections').then((mod) => mod.StreamerCollectionsSection),
  { loading: () => null, ssr: false }
);

// const collectionsFilters: ITagFilterItem[] = [
//   {
//     id: 'twitch',
//     icon: <Icon.TwitchWhite width="20" height="20" />,
//     text: 'twitch streamers',
//     type: 'navigate',
//     destination: Routes.streamersCollections('twitch'),
//   },
//   {
//     id: 'youtube',
//     icon: <Icon.YoutubeWhite />,
//     text: 'YouTube streamers',
//     type: 'navigate',
//     destination: Routes.streamersCollections('youtube'),
//   },

//   {
//     id: 'kick',
//     icon: <Icon.KickWhite width="14" height="12" />,
//     text: 'Kick Streamers',
//     type: 'navigate',
//     destination: Routes.streamersCollections('kick'),
//   },

//   {
//     id: 'casino-cheaters',
//     icon: <Icon.AlertSecond fill="white" />,
//     text: 'Cheaters',
//     type: 'navigate',
//     destination: Routes.streamersCollections('casino-cheaters'),
//   },
// ];

// const tagsFilters: ITagFilterItem[] = [
//   {
//     id: 'top10',
//     icon: <Icon.Trophy />,
//     text: 'Top 10',
//   },
//   {
//     id: 'cheaters',
//     icon: <Icon.AlertSecond />,
//     text: 'Cheaters',
//   },
//   {
//     id: 'honest',
//     icon: <Icon.Like />,
//     text: 'Honest',
//   },
//   {
//     id: 'fakeMoney',
//     icon: <Icon.Blacklisted />,
//     text: 'Fake Money',
//   },
//   {
//     id: 'crypto',
//     icon: <Icon.Cryptocoin />,
//     text: 'Crypto Streamers',
//   },
// ];

const capitalizeFirstLetter = (string: string | null | undefined) => {
  if (!string || !string?.length) return null;

  return string?.charAt(0)?.toUpperCase() + string?.slice(1);
};

const NoStreamers = () => {
  return (
    <div className={styles.empty_data}>
      <p className={styles.message}>{'No streamers matching your criteria.'}</p>
    </div>
  );
};

interface AllStreamersProps {
  data: IStreamer[];
  showMore?: boolean;
  hasSsrData?: boolean;
}

export const AllStreamers = ({
  data,
  showMore,
  hasSsrData = false,
}: AllStreamersProps) => {
  if (!data?.length) {
    if (hasSsrData) {
      return null;
    }

    return <CardsSkeleton />;
  }

  return (
    <div className={clsx(styles.card_container)}>
      <div className={styles.allstreamers_container}>
        {data.map((item, index) => (
          <StreamerCard
            key={item?._id}
            item={item}
            isLink
            variant={'main'}
            priority={index === 0}
          />
        ))}
      </div>
      {showMore && (
        <div className={styles.showmore_container}>
          <SeeMore
            variant="small"
            description={'Show more'}
            url={Routes.streamersAll}
          />
        </div>
      )}
    </div>
  );
};

interface StreamersProps {
  hideCollections?: boolean;
  reviewsCount?: number;
  streamers: IStreamer[];
  streamersCount?: number;
  languages?: string[];
  reviews?: IStreamerReview[];
  collections?: IStreamerCollection[];
}

export const Streamers = ({
  hideCollections,
  streamers,
  streamersCount = 0,
  reviews = [],
  languages,
  collections = [],
  reviewsCount = 0,
}: StreamersProps) => {
  const router = useRouter();
  const { query } = router;
  const [languageOptions, setLanguageOptions] = useState<string[]>(
    languages ?? []
  );

  useEffect(() => {
    if (languageOptions.length > 0) {
      return;
    }

    listStreamerLanguages()
      .then(({ languages: results }) => setLanguageOptions(results ?? []))
      .catch(() => setLanguageOptions([]));
  }, [languageOptions.length]);

  const parseInitialQueryFilters = (allowedKeys: string[] = []) => {
    const initialQueryFilters: any = { sortBy: 'popular' };

    Object.keys(query).forEach((key) => {
      if (query[key] && allowedKeys.includes(key)) {
        initialQueryFilters[key] = query[key] as string;
      }
    });

    return initialQueryFilters;
  };

  const defaultSelectedFilters: any = useMemo(() => {
    return {
      sortBy: 'popular',
    };
  }, []);

  const [submittedTimestamp, setSubmittedTimestamp] = useState<number>(0);
  const [selectedFilters, setSelectedFilters] = useState(
    parseInitialQueryFilters(['sortBy', 'languages', 'socials'])
  );
  const selectedFiltersRef = useRef(selectedFilters);

  // const [selectedTagsFilters, setSelectedTagsFilters] = useState(
  //   parseInitialQueryFilters([
  //     'top10',
  //     'cheaters',
  //     'honest',
  //     'fakeMoney',
  //     'crypto',
  //   ])
  // );

  const selectedQueryFilters = useMemo(() => {
    const filters = { sortBy: 'popular' };

    Object.keys(query).forEach((key) => {
      if (query[key]) {
        (filters as any)[key] = query[key];
      }
    });
    return filters;
  }, [query]);

  const updateQueryParams = useCallback(
    (filters: object) => {
      const updatedQuery = {};

      Object.keys(filters).forEach((key) => {
        if ((filters as any)[key]) {
          (updatedQuery as any)[key] = (filters as any)[key];
        }
      });

      router.push(
        {
          pathname: router.pathname,
          query: updatedQuery,
        },
        undefined,
        {
          shallow: true,
        }
      );
    },
    [router]
  );

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  const applyFilters = useCallback(async () => {
    updateQueryParams({
      /*...selectedTagsFilters,*/ ...selectedFiltersRef.current,
    });
  }, [updateQueryParams]);

  useEffect(() => {
    if (submittedTimestamp === 0) {
      return;
    }

    applyFilters();
  }, [applyFilters, submittedTimestamp]);

  const {
    page,
    isLast,
    setPage,
    isFetching,
    data: streamersPagination,
  } = useStreamersPagination({
    filters: selectedQueryFilters,
    initialData: streamers,
    initialCount: streamersCount,
  });

  const streamersData = streamersPagination.length
    ? streamersPagination
    : streamers;

  const filters: IFilter[] = useMemo(
    () => [
      {
        key: 'sortBy',
        isSingle: true,
        text: `Sort by: ${
          capitalizeFirstLetter(selectedFilters?.sortBy) ?? 'Popular'
        }`,
        title: 'Sort by',
        type: 'common',
        items: [
          { _id: 'popular', name: 'Most popular' } as IRegionItem,
          { _id: 'new', name: 'New streamers' } as IRegionItem,
        ],
      },
      {
        key: 'languages',
        isSingle: true,
        text: `Languages: ${
          capitalizeFirstLetter(selectedFilters?.languages) ?? 'All'
        }`,
        title: 'Choosing a language',
        items: languageOptions?.map(
          (language) =>
            ({
              _id: language,
              name: language,
            }) as unknown as IRegionItem
        ),
        type: 'common',
      },
      {
        key: 'socials',
        isSingle: true,
        text: `Platform: ${
          capitalizeFirstLetter(selectedFilters?.socials) ?? 'All'
        }`,
        items: [
          { _id: 'twitch', name: 'Twitch' } as IRegionItem,
          { _id: 'youtube', name: 'YouTube' } as IRegionItem,
          { _id: 'kick', name: 'Kick' } as IRegionItem,
        ],
        title: 'Choose a platform',
        type: 'common',
      },
    ],
    [
      selectedFilters.sortBy,
      selectedFilters?.languages,
      selectedFilters?.socials,
      languageOptions,
    ]
  );

  return (
    <>
      <CustomMetadata metadata={allMetadata.streamers} />
      <Layout hasFooter isSearchInputMobileVisible>
        <main className={styles.main}>
          <div className={styles.container}>
            <div className={styles.breadcrumbs}>
              <Breadcrumbs items={[...streamerPage]} />
            </div>
            <Title
              text="Online Casino Streamer Rating"
              description="Find detailed reviews of streamers, their biggest wins, games played with fake money, and how streamers deceive players."
              gap={10}
            />

            <Search variant="streamer" placeholder="Search for streamers" />
            <StreamersFiltersBar
              filters={filters}
              selectedFilters={selectedFilters}
              defaultSelectedFilters={defaultSelectedFilters}
              onFieldChange={(key, value) => {
                setSelectedFilters((prev: any) => ({
                  ...prev,
                  [key]: value,
                }));
              }}
              onSubmit={() => setSubmittedTimestamp(Date.now())}
              onClear={() => {
                setSelectedFilters(defaultSelectedFilters);
                setSubmittedTimestamp(Date.now());
              }}
            />

            <AllStreamers
              data={streamersData}
              showMore={!hideCollections}
              hasSsrData={streamers.length > 0}
            />

            {!hideCollections && collections.length > 0 && (
              <StreamerCollectionsSection collections={collections} />
            )}

            {!hideCollections && reviews.length > 0 && (
              <div className={styles.latestReviews}>
                <LatestReviewsSection
                  data={reviews}
                  count={reviewsCount}
                  title="Latest Streamers Reviews"
                />
              </div>
            )}

            {!streamersPagination?.length && !isFetching && <NoStreamers />}
            {!isLast && (
              <>
                <LoadMoreButton
                  isAuto={false}
                  isFetching={isFetching}
                  onClick={() => {
                    if (!streamersPagination?.length) return;
                    setPage(page + 1);
                  }}
                />
                <div className={styles.bottom} />
              </>
            )}

            <StreamersDescriptionSection />

            <FAQSection
              titleClassName={styles.faq_title}
              customFaq={[
                {
                  title:
                    'How long does it take to become a successful casino streamer?',
                  description:
                    'It depends on your resources and audience-building skills. Most successful streamers have partnerships that accelerate their growth.',
                },
                {
                  title: 'Do streamers play with real money?',
                  description:
                    'Many streamers use fake money or promotional funds, making their winnings unrealistic.',
                },
                {
                  title: 'Can I trust casino streamers?',
                  description:
                    'Most cannot be trusted due to deceptive practices. Honest streamers are extremely rare.',
                },
                {
                  title: 'Which platform is best for casino streams?',
                  description:
                    'Twitch, Kick, and YouTube are the top platforms, each with its pros and cons.',
                },
                {
                  title: 'How do streamers earn money?',
                  description:
                    'Streamers make money through ads, sponsorships, and affiliate deals with casinos.',
                },
                {
                  title: 'Why do casinos sponsor streamers?',
                  description:
                    'Casinos see streamers as an effective marketing tool to attract new players.',
                },
                {
                  title: 'What should I watch out for in gambling streams?',
                  description:
                    'Beware of fake wins, scam casinos, and misleading claims about gambling success.',
                },
              ]}
            />
          </div>
        </main>
      </Layout>
    </>
  );
};
